// src/debug.js
//
// Runtime debug toggle. Persists in localStorage. Hidden from regular users:
//
//   • Keyboard shortcut: Ctrl+Shift+D (Cmd+Shift+D on Mac)
//   • URL query param:   ?debug=on  or  ?debug=off
//
// When enabled, a small clickable "🐞 DEBUG" pill appears in the bottom-right
// corner. Click it (or press the shortcut again) to disable.
//
// Usage in components:
//
//   import { useDebug } from './debug';
//   const [debug] = useDebug();
//   {debug && <small>internal hint here</small>}
//
// And render <DebugBadge /> once at the App root.

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'wc_debug';
const EVENT = 'wc-debug-change';

// ---- Initialization ----
// Honor URL param on first load (?debug=on / off / 1 / 0 / true / false).
// Falls through to whatever's in localStorage.
const readInitial = () => {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = (params.get('debug') || '').toLowerCase();
    if (fromUrl === 'on' || fromUrl === '1' || fromUrl === 'true') {
      localStorage.setItem(STORAGE_KEY, '1');
    } else if (fromUrl === 'off' || fromUrl === '0' || fromUrl === 'false') {
      localStorage.removeItem(STORAGE_KEY);
    }
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

let _debug = readInitial();

// ---- Public API ----

export const getDebug = () => _debug;

export const setDebug = (val) => {
  _debug = !!val;
  try {
    if (_debug) localStorage.setItem(STORAGE_KEY, '1');
    else localStorage.removeItem(STORAGE_KEY);
  } catch {}
  try {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: _debug }));
  } catch {}
};

// ---- Keyboard shortcut, mounted exactly once ----
let keyMounted = false;
const mountKeyListener = () => {
  if (keyMounted || typeof window === 'undefined') return;
  keyMounted = true;
  window.addEventListener('keydown', (e) => {
    // Ctrl+Shift+D or Cmd+Shift+D, but ignore when typing in inputs
    if (
      (e.ctrlKey || e.metaKey) &&
      e.shiftKey &&
      (e.key === 'D' || e.key === 'd')
    ) {
      const tag = (e.target?.tagName || '').toLowerCase();
      const isTextField =
        tag === 'input' || tag === 'textarea' || e.target?.isContentEditable;
      if (isTextField) return; // don't hijack while typing
      e.preventDefault();
      setDebug(!getDebug());
    }
  });
};

// ---- React hook ----

export function useDebug() {
  const [val, setLocal] = useState(getDebug());
  useEffect(() => {
    mountKeyListener();
    const handler = (e) => setLocal(!!e.detail);
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
  }, []);
  return [val, setDebug];
}

// ---- Floating badge — render once at the App root ----

export function DebugBadge() {
  const [debug] = useDebug();
  if (!debug) return null;
  return (
    <button
      type="button"
      onClick={() => setDebug(false)}
      title="Debug mode is on. Click or press Ctrl/Cmd+Shift+D to turn off."
      style={{
        position: 'fixed',
        bottom: 12,
        right: 12,
        zIndex: 99999,
        padding: '6px 12px',
        borderRadius: 999,
        border: '1px solid rgba(220, 38, 38, 0.5)',
        background: 'rgba(220, 38, 38, 0.92)',
        color: 'white',
        fontWeight: 800,
        fontSize: 12,
        letterSpacing: 0.5,
        cursor: 'pointer',
        boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
        fontFamily: 'Arial',
      }}
    >
      🐞 DEBUG
    </button>
  );
}
